import React, { useState, useMemo } from 'react';
import { VirtualTrade } from '../types';

interface ReplayTradingPanelProps {
  trades: VirtualTrade[];
  balance: number;
  initialBalance: number;
  equity: number;
  timezone: string;
}

type Tab = 'overview' | 'performance' | 'list';

const ReplayTradingPanel: React.FC<ReplayTradingPanelProps> = ({
  trades,
  balance,
  initialBalance,
  equity,
  timezone
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('list');

  // --- Metrics Calculation ---
  const metrics = useMemo(() => {
    const closedTrades = trades.filter(t => t.status === 'CLOSED');
    const openTrades = trades.filter(t => t.status === 'OPEN');
    
    const totalClosed = closedTrades.length;
    const winningTrades = closedTrades.filter(t => (t.pnl || 0) > 0);
    const losingTrades = closedTrades.filter(t => (t.pnl || 0) <= 0);
    
    const grossProfit = winningTrades.reduce((acc, t) => acc + (t.pnl || 0), 0);
    const grossLoss = losingTrades.reduce((acc, t) => acc + (t.pnl || 0), 0);
    const netProfit = grossProfit + grossLoss;
    
    const winRate = totalClosed > 0 ? (winningTrades.length / totalClosed) * 100 : 0;
    const profitFactor = Math.abs(grossLoss) > 0 ? grossProfit / Math.abs(grossLoss) : grossProfit > 0 ? Infinity : 0;
    
    const avgWin = winningTrades.length > 0 ? grossProfit / winningTrades.length : 0;
    const avgLoss = losingTrades.length > 0 ? grossLoss / losingTrades.length : 0;
    
    // Max Drawdown Calculation (Simulated based on closed trade sequence)
    let runningBalance = initialBalance;
    let peakBalance = initialBalance;
    let maxDrawdown = 0;
    
    // Sort by exit time for accurate curve
    const sortedClosed = [...closedTrades].sort((a, b) => (a.exitTime || 0) - (b.exitTime || 0));
    
    sortedClosed.forEach(t => {
        runningBalance += (t.pnl || 0);
        if (runningBalance > peakBalance) peakBalance = runningBalance;
        const drawdown = peakBalance - runningBalance;
        if (drawdown > maxDrawdown) maxDrawdown = drawdown;
    });

    return {
      totalClosed,
      openCount: openTrades.length,
      netProfit,
      grossProfit,
      grossLoss,
      winRate,
      profitFactor,
      maxDrawdown,
      avgWin,
      avgLoss,
      returnPct: ((equity - initialBalance) / initialBalance) * 100
    };
  }, [trades, initialBalance, equity]);

  // Helper to format currency
  const fmtMoney = (val: number) => val.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  const fmtPct = (val: number) => `${val.toFixed(2)}%`;

  return (
    <div className="flex flex-col h-full bg-slate-900 text-slate-200">
      {/* Toolbar */}
      <div className="flex items-center border-b border-slate-800 px-4 h-10 space-x-6 shrink-0">
        <button 
            onClick={() => setActiveTab('list')}
            className={`text-xs font-bold uppercase tracking-wide h-full border-b-2 transition-colors ${activeTab === 'list' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
        >
            List of Trades
        </button>
        <button 
            onClick={() => setActiveTab('performance')}
            className={`text-xs font-bold uppercase tracking-wide h-full border-b-2 transition-colors ${activeTab === 'performance' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
        >
            Performance Summary
        </button>
        <button 
            onClick={() => setActiveTab('overview')}
            className={`text-xs font-bold uppercase tracking-wide h-full border-b-2 transition-colors ${activeTab === 'overview' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
        >
            Account Overview
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-4">
        
        {/* LIST OF TRADES */}
        {activeTab === 'list' && (
            <div className="h-full flex flex-col">
                <div className="overflow-auto rounded-lg border border-slate-800">
                    <table className="w-full text-left text-xs">
                        <thead className="bg-slate-800/50 text-slate-400 font-medium sticky top-0">
                            <tr>
                                <th className="p-3">ID</th>
                                <th className="p-3">Type</th>
                                <th className="p-3">Symbol</th>
                                <th className="p-3 text-right">Qty</th>
                                <th className="p-3 text-right">Entry Price</th>
                                <th className="p-3 text-right">Exit Price</th>
                                <th className="p-3">Entry Time</th>
                                <th className="p-3">Exit Time</th>
                                <th className="p-3 text-right">P&L</th>
                                <th className="p-3 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {[...trades].reverse().map((trade) => (
                                <tr key={trade.id} className="hover:bg-slate-800/30 transition-colors">
                                    <td className="p-3 font-mono text-slate-500">{trade.id.slice(-6)}</td>
                                    <td className="p-3">
                                        <span className={`px-1.5 py-0.5 rounded font-bold ${trade.type === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                                            {trade.type}
                                        </span>
                                    </td>
                                    <td className="p-3 font-bold text-slate-300">{trade.symbol}</td>
                                    <td className="p-3 text-right font-mono">{trade.quantity}</td>
                                    <td className="p-3 text-right font-mono">{trade.entryPrice.toFixed(2)}</td>
                                    <td className="p-3 text-right font-mono">{trade.exitPrice?.toFixed(2) || '-'}</td>
                                    <td className="p-3 text-slate-500">
                                        {new Date(trade.entryTime).toLocaleString('en-US', {
                                            timeZone: timezone,
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: false
                                        })}
                                    </td>
                                    <td className="p-3 text-slate-500">
                                        {trade.exitTime ? new Date(trade.exitTime).toLocaleString('en-US', {
                                            timeZone: timezone,
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: false
                                        }) : '-'}
                                    </td>
                                    <td className={`p-3 text-right font-bold font-mono ${(trade.pnl || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                        {trade.pnl ? fmtMoney(trade.pnl) : '-'}
                                    </td>
                                    <td className="p-3 text-center">
                                        <span className={`text-[10px] uppercase font-bold ${trade.status === 'OPEN' ? 'text-blue-400' : 'text-slate-500'}`}>
                                            {trade.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {trades.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="p-8 text-center text-slate-500 italic">No trades recorded yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        )}

        {/* PERFORMANCE SUMMARY */}
        {activeTab === 'performance' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Net Profit */}
                <div className="bg-slate-800/30 border border-slate-800 p-4 rounded-xl">
                    <span className="text-xs text-slate-500 uppercase font-bold">Net Profit</span>
                    <div className={`text-2xl font-mono font-bold mt-1 ${metrics.netProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {fmtMoney(metrics.netProfit)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        {fmtPct((metrics.netProfit / initialBalance) * 100)} return
                    </div>
                </div>

                {/* Profit Factor */}
                <div className="bg-slate-800/30 border border-slate-800 p-4 rounded-xl">
                    <span className="text-xs text-slate-500 uppercase font-bold">Profit Factor</span>
                    <div className="text-2xl font-mono font-bold mt-1 text-slate-200">
                        {metrics.profitFactor.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        Gross: <span className="text-emerald-400">{fmtMoney(metrics.grossProfit)}</span> / <span className="text-rose-400">{fmtMoney(metrics.grossLoss)}</span>
                    </div>
                </div>

                {/* Win Rate */}
                <div className="bg-slate-800/30 border border-slate-800 p-4 rounded-xl">
                    <span className="text-xs text-slate-500 uppercase font-bold">Percent Profitable</span>
                    <div className="text-2xl font-mono font-bold mt-1 text-blue-400">
                        {fmtPct(metrics.winRate)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        {metrics.totalClosed} Trades Total
                    </div>
                </div>

                {/* Max Drawdown */}
                <div className="bg-slate-800/30 border border-slate-800 p-4 rounded-xl">
                    <span className="text-xs text-slate-500 uppercase font-bold">Max Drawdown</span>
                    <div className="text-2xl font-mono font-bold mt-1 text-rose-400">
                        {fmtMoney(metrics.maxDrawdown)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        Peak-to-Valley
                    </div>
                </div>

                {/* Average Trade */}
                <div className="bg-slate-800/30 border border-slate-800 p-4 rounded-xl">
                    <span className="text-xs text-slate-500 uppercase font-bold">Avg Trade</span>
                    <div className="flex justify-between items-end mt-1">
                        <div>
                            <span className="text-xs text-slate-400 block">Win</span>
                            <span className="text-lg font-mono font-bold text-emerald-400">{fmtMoney(metrics.avgWin)}</span>
                        </div>
                        <div className="text-right">
                            <span className="text-xs text-slate-400 block">Loss</span>
                            <span className="text-lg font-mono font-bold text-rose-400">{fmtMoney(metrics.avgLoss)}</span>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* ACCOUNT OVERVIEW */}
        {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Main Stats */}
                <div className="md:col-span-1 space-y-4">
                    <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                        <h4 className="text-sm text-slate-400 font-medium mb-2">Account Balance</h4>
                        <div className="text-3xl font-bold text-white tracking-tight">
                            {fmtMoney(equity)}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                            <span className={`text-sm font-bold ${metrics.netProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {metrics.netProfit >= 0 ? '+' : ''}{fmtMoney(metrics.netProfit)}
                            </span>
                            <span className="text-xs text-slate-500">Total P&L</span>
                        </div>
                    </div>

                    <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                        <h4 className="text-sm text-slate-400 font-medium mb-2">Open Positions</h4>
                        <div className="text-3xl font-bold text-white tracking-tight">
                            {metrics.openCount}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">Active trades currently running</div>
                    </div>
                </div>

                {/* Placeholder for Chart (could implement Recharts here later for equity curve) */}
                <div className="md:col-span-2 bg-slate-800/30 border border-slate-800 rounded-xl p-6 flex flex-col items-center justify-center text-slate-600">
                    <svg className="w-16 h-16 mb-4 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                    <span className="text-sm font-medium">Equity Curve Visualization</span>
                    <span className="text-xs mt-1">(Coming Soon)</span>
                </div>
            </div>
        )}

      </div>
    </div>
  );
};

export default ReplayTradingPanel;